import React, { createContext, useContext, useState, useEffect } from 'react';

interface User {
  id?: string; // core users.id — rides as X-User-Id on core writes
  email: string;
  name: string;
  picture: string;
  role?: string; // core users.role — the Assistant is offered to admins only
}

interface AuthContextType {
  user: User | null;
  login: (credential: string) => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const CORE_URL = process.env.REACT_APP_CORE_URL || 'https://core-service.bhuvanafortunegreens.com';
// Every sign-in names this portal's surface — core refuses a user whose
// `surfaces` allowlist does not carry it (MAG-174).
const SURFACE = 'marketing';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('marketing_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const login = async (credential: string) => {
    const base64Url = credential.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    const userData = JSON.parse(jsonPayload);

    // Core's users table is the gate (§3.9) — same as the password path;
    // also refreshes name/picture on the user row from Google.
    const response = await fetch(`${CORE_URL}/api/v1/auth/verify-user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: userData.email,
        name: userData.name,
        picture_url: userData.picture,
        google_id: userData.sub,
        surface: SURFACE,
      }),
    });
    if (!response.ok) {
      const detail = await response.json().then((d) => d.detail).catch(() => null);
      throw new Error(detail || 'Login failed');
    }
    const data = await response.json();

    const userInfo: User = {
      id: data.id,
      email: data.email,
      name: data.name || data.email,
      picture: data.google_picture_url || userData.picture || '',
      role: data.role,
    };

    setUser(userInfo);
    localStorage.setItem('marketing_user', JSON.stringify(userInfo));
  };

  const loginWithPassword = async (email: string, password: string) => {
    // Core's users table is the gate — same endpoint the nurture app and
    // touchpoint portal use; 403 for anything invalid.
    const response = await fetch(`${CORE_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, surface: SURFACE }),
    });
    if (!response.ok) {
      const detail = await response.json().then((d) => d.detail).catch(() => null);
      throw new Error(detail || 'Login failed');
    }
    const data = await response.json();
    const userInfo: User = {
      id: data.id,
      email: data.email,
      name: data.name || data.email,
      picture: data.google_picture_url || '',
      role: data.role,
    };
    setUser(userInfo);
    localStorage.setItem('marketing_user', JSON.stringify(userInfo));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('marketing_user');
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithPassword, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
